import { MarkerType } from "@xyflow/react";
import type { RuleGraphDto } from "@intelligems/ig-types";
import type { Theme } from "@mui/material/styles";
import { cartInputTargetId } from "../layout";
import { layoutRuleGraph } from "../layout";
import { resolvedRuleGraphEdges } from "../layout";
import type { RuleGraphLayout } from "../layout";
import type { RuleGraphFlowEdge } from "../ruleGraphNodeData/RuleGraphFlowEdge";
import type { RuleGraphOverlaysByEdgeId } from "../types";
import type { RuleGraphOverlaysByNodeId } from "../types";
import { edgeOverlayId } from "../types";
import { RULE_GRAPH_CART_INPUT_NODE_ID } from "../types";
import { hiddenRuleGraphNodeIds } from "./hiddenRuleGraphNodeIds";

export const getRuleGraphFlowEdges = (
  graph: RuleGraphDto,
  theme: Theme,
  overlaysByEdgeId?: RuleGraphOverlaysByEdgeId,
  overlaysByNodeId?: RuleGraphOverlaysByNodeId,
  suppliedLayout?: RuleGraphLayout
): RuleGraphFlowEdge[] => {
  const hasScenarioOverlay = overlaysByEdgeId !== undefined;
  const layout = suppliedLayout ?? layoutRuleGraph(graph);
  const { mode, laneOrder, leadInNodeIds } = layout;
  // The input heads a row of its own when it is the only header (branchless)
  // or when shared lead-in steps run ahead of the branch rows.
  const headerInputId =
    mode === "branchless" || leadInNodeIds.length > 0
      ? graph.nodes.find((node) => node.kind === "input")?.id
      : undefined;
  const hidden =
    mode === "base"
      ? new Set<string>()
      : hiddenRuleGraphNodeIds(
          graph,
          overlaysByNodeId,
          headerInputId !== undefined
        );
  const kindById = new Map(graph.nodes.map((node) => [node.id, node.kind]));
  const branchIdByNodeId = new Map(
    graph.nodes.map((node) => [node.id, node.branchId])
  );
  const leadIn = new Set(leadInNodeIds);
  /**
   * Where a fallback edge into another row or the terminal joins the left spine.
   *
   * The rows draw their fallback chain down the lane dots, so an edge that
   * starts on a CARD has no honest line to draw — from the card's underside it
   * runs the width of the row to reach the terminal's dot. Every such source
   * is projected onto the lane header that owns it: its own row's header when
   * the step names a row, and otherwise the last row's header, because a step
   * that runs after every row (the platform exclusions tail, a branchless
   * pipeline's last card) falls through from the bottom of the stack.
   *
   * Lane modes only: the base pass draws a raw DAG with no spine to join.
   */
  const spineAnchorId = (fromId: string): string => {
    if (mode === "base") {
      return fromId;
    }
    if (kindById.get(fromId) === "branch" || fromId === headerInputId) {
      return fromId;
    }
    // A lead-in step joins the spine at the input row it runs under; a step
    // after every row falls through from the bottom of the stack.
    if (leadIn.has(fromId)) {
      return headerInputId ?? fromId;
    }
    return (
      branchIdByNodeId.get(fromId) ??
      headerInputId ??
      laneOrder[laneOrder.length - 1] ??
      fromId
    );
  };

  // The synthetic edge from the run's cart into a row header. It exists
  // exactly when the cart node does (its overlay carries the cart), so a
  // canvas with no run draws no dangling arrow. The run decides WHICH row:
  // the overlay seam already names the edge (`__cart-input__-><row>:flow`),
  // and that key is the target; with no such key the cart enters the first
  // row.
  const cartTargetId =
    Object.keys(overlaysByEdgeId ?? {})
      .find((id) => id.startsWith(`${RULE_GRAPH_CART_INPUT_NODE_ID}->`))
      ?.slice(RULE_GRAPH_CART_INPUT_NODE_ID.length + 2)
      .replace(/:flow$/, "") ?? cartInputTargetId(graph, layout);
  const cartEdges: { from: string; to: string; kind: "flow" }[] =
    overlaysByNodeId?.[RULE_GRAPH_CART_INPUT_NODE_ID]?.cartInput !==
      undefined && cartTargetId !== undefined
      ? [
          {
            from: RULE_GRAPH_CART_INPUT_NODE_ID,
            to: cartTargetId,
            kind: "flow",
          },
        ]
      : [];

  return [...cartEdges, ...resolvedRuleGraphEdges(graph)]
    .filter(
      (edge) =>
        // The cart edge is the renderer's own and never hidden.
        edge.from === RULE_GRAPH_CART_INPUT_NODE_ID ||
        // An edge into or out of a hidden node has nothing to attach to.
        (!hidden.has(edge.from) &&
          !hidden.has(edge.to) &&
          // A lane header draws no arrow into its own pipeline — the cards
          // simply sit beneath it, as in the Figma rows.
          !(
            mode !== "base" &&
            edge.kind === "flow" &&
            (kindById.get(edge.from) === "branch" ||
              edge.from === headerInputId)
          ) &&
          // Nor does flow arrive at a branch header by arrow: a row entered
          // from the lead-in's last card sits beneath that row, and the lane
          // dots already say so. Lane modes only — the base pass is a raw DAG.
          !(
            mode !== "base" &&
            edge.kind === "flow" &&
            kindById.get(edge.to) === "branch"
          ))
    )
    .map((edge) => {
      const edgeOverlay = overlaysByEdgeId?.[edgeOverlayId(edge)];
      const dimmed = edgeOverlay?.dimmed ?? false;
      // The run walk's rail beat: the fallback edge under the cursor thickens
      // and takes the highlight color while it "fills".
      const active = edgeOverlay?.active === true;
      const color =
        hasScenarioOverlay && !dimmed
          ? theme.palette.text.interactive.neutral.default
          : theme.igBackgroundColors.interactive.dark.disabled;
      // Keep the runtime edge id, but attach the line to the lane header that
      // owns the step, so the fallback into the next row stays the vertical
      // spine down the lane dots instead of a line across the row.
      const source =
        edge.kind === "fallback" &&
        (kindById.get(edge.to) === "branch" ||
          kindById.get(edge.to) === "fallback-terminal")
          ? spineAnchorId(edge.from)
          : edge.from;

      return {
        id: edgeOverlayId(edge),
        source,
        sourceHandle:
          edge.kind === "fallback" ? "fallback-source" : "flow-source",
        target: edge.to,
        targetHandle:
          edge.kind === "fallback" ? "fallback-target" : "flow-target",
        // Flow edges are the custom curve (`RuleGraphFlowEdge`): a shared
        // tail step — the platform exclusions card, the output — sits
        // vertically centered across the rows, so a row above or below it has
        // a source and a target on different lines. Drawn straight, that edge
        // leaves the card's right handle as a long diagonal across the canvas;
        // stepped, the 28px card gap leaves no room for a corner radius.
        // Fallback edges stay straight: they are the vertical spine down the
        // lane dots, where a router has nothing to route around.
        type: edge.kind === "flow" ? "ruleGraphFlow" : "straight",
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color,
          height: 8,
          width: 8,
        },
        style: {
          stroke: color,
          strokeWidth: active ? 2 : 1.5,
          opacity: dimmed ? 0.35 : 1,
          // Reached edges settle their color/weight rather than snapping —
          // the run walk's approximation of the prototype's rail fill.
          transition:
            "stroke 200ms ease, stroke-width 200ms ease, opacity 200ms ease",
        },
      };
    });
};
